// The three things a tutor can assign from the Diagnostics tab: a base
// diagnostic, a prerequisite test built from an earlier one, and the Skill
// Builders that remediate what either of them found.
//
// All three panels stay mounted behind the switch, so moving between them does
// not reset a half-built selection.

import { useState } from "react"
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap"
import classnames from "classnames"
import type { BmdStagedDraft } from "../types/bmd.types"
import type { BmdPrereqStagedDraft } from "../types/bmd-prereq.types"
import type { BmdSkillbuilderStagedDraft } from "../types/bmd-skillbuilder.types"
import { BmdTabPanel } from "./BmdTabPanel"
import { BmdPrereqTabPanel } from "./BmdPrereqTabPanel"
import { BmdSkillbuilderTabPanel } from "./BmdSkillbuilderTabPanel"

type BmdAssignPanelProps = {
  subjectId: number
  studentKey: string
  onStageDraft: (draft: BmdStagedDraft) => void
  /** Prerequisite tests already staged in this visit. */
  prereqStagedKeys?: string[]
  onStagePrereqDraft: (draft: BmdPrereqStagedDraft) => void
  /** Skill Builders already staged in this visit. */
  skillbuilderStagedIds?: number[]
  onStageSkillbuilderDraft: (draft: BmdSkillbuilderStagedDraft) => void
}

type BmdAssignMode = "test" | "prereq" | "skillbuilder"

export function BmdAssignPanel({
  subjectId,
  studentKey,
  onStageDraft,
  prereqStagedKeys = [],
  onStagePrereqDraft,
  skillbuilderStagedIds = [],
  onStageSkillbuilderDraft,
}: BmdAssignPanelProps) {
  const [mode, setMode] = useState<BmdAssignMode>("test")

  // Every pill gets a box so the inactive ones still read as controls. The
  // active one keeps the theme's solid fill; the transparent border on it
  // stops the pills resizing when the selection changes.
  const pillStyle = (isActive: boolean) => ({
    cursor: "pointer",
    border: `1px solid ${isActive ? "transparent" : "#d5d8e2"}`,
    backgroundColor: isActive ? undefined : "#ffffff",
    color: isActive ? undefined : "#5b6270",
    fontWeight: 600,
  })

  return (
    <div>
      <Nav pills className="gap-2 px-3" style={{ marginBottom: "10px" }}>
        <NavItem>
          <NavLink
            style={pillStyle(mode === "test")}
            className={classnames("font-size-13", {
              active: mode === "test",
            })}
            onClick={() => setMode("test")}
          >
            Diagnostics Test
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            style={pillStyle(mode === "prereq")}
            className={classnames("font-size-13", {
              active: mode === "prereq",
            })}
            onClick={() => setMode("prereq")}
          >
            Prerequisite Test
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            style={pillStyle(mode === "skillbuilder")}
            className={classnames("font-size-13", {
              active: mode === "skillbuilder",
            })}
            onClick={() => setMode("skillbuilder")}
          >
            Skill Builders
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={mode}>
        <TabPane tabId="test">
          <BmdTabPanel
            subjectId={subjectId}
            studentKey={studentKey}
            onStageDraft={onStageDraft}
          />
        </TabPane>
        <TabPane tabId="prereq">
          <BmdPrereqTabPanel
            studentKey={studentKey}
            stagedKeys={prereqStagedKeys}
            onStageDraft={onStagePrereqDraft}
          />
        </TabPane>
        <TabPane tabId="skillbuilder">
          <BmdSkillbuilderTabPanel
            studentKey={studentKey}
            stagedSkillbuilderIds={skillbuilderStagedIds}
            onStageDraft={onStageSkillbuilderDraft}
          />
        </TabPane>
      </TabContent>
    </div>
  )
}

export default BmdAssignPanel
